// Product Links data layer (Phase 4, P4-WP10) — related / similar / upsell links
// between a company's OWN products (for WP12 cross-sell surfacing). The SINGLE
// interface between the app and public.product_links
// (supabase/migrations/20260703103000_catalogue_media.sql).
//
// A DB trigger rejects cross-company links (related_product_id must share the
// company); a CHECK rejects self-links. company_id is trigger-owned; we never send it.

import { supabase } from "./supabaseClient";

const T = "product_links";

export const LINK_TYPES = [
  { value: "related", label: "Related" },
  { value: "similar", label: "Similar" },
  { value: "upsell", label: "Upsell" },
];

// Disambiguated embed of the TARGET product (two FKs to products exist on this table).
const LINK_SELECT =
  "id, product_id, related_product_id, link_type, position, " +
  "related:products!product_links_related_product_id_fkey ( id, name, product_number, sku )";

function client() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}
function toError(error, fallback) {
  const code = error && error.code;
  let message;
  switch (code) {
    case "23505": message = "That product is already linked with this type."; break;
    case "23514": message = "A product cannot be linked to itself."; break;
    case "23503": message = "One of the products does not exist."; break;
    case "42501": message = "Related product must belong to your company."; break;
    case "PGRST116": message = "Link not found, or you do not have access to it."; break;
    default: message = (error && error.message) || fallback;
  }
  const e = new Error(message); e.code = code; e.cause = error;
  return e;
}

/** Links from a product, with the target product embedded, ordered. */
export async function getLinks(productId) {
  const { data, error } = await client().from(T).select(LINK_SELECT)
    .eq("product_id", productId).order("link_type", { ascending: true }).order("position", { ascending: true });
  if (error) throw toError(error, "Could not load related products");
  return data || [];
}

/**
 * Link a related/similar/upsell product. related_product_id must be a different
 * product in the same company (DB-enforced).
 * @returns {Promise<string>} new link id.
 */
export async function addLink(productId, relatedProductId, linkType = "related") {
  if (productId === relatedProductId) { const e = new Error("A product cannot be linked to itself."); e.code = "23514"; throw e; }
  const { data, error } = await client().from(T)
    .insert({ product_id: productId, related_product_id: relatedProductId, link_type: linkType })
    .select("id").single();
  if (error) throw toError(error, "Could not add the link");
  return data.id;
}

/** Remove a link. */
export async function removeLink(id) {
  const { error } = await client().from(T).delete().eq("id", id);
  if (error) throw toError(error, "Could not remove the link");
}

/** Persist a new order within a link group. */
export async function reorderLinks(orderedIds = []) {
  const c = client();
  for (let i = 0; i < orderedIds.length; i++) {
    const { error } = await c.from(T).update({ position: i }).eq("id", orderedIds[i]);
    if (error) throw toError(error, "Could not reorder links");
  }
}
